import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { observer } from 'mobx-react'

import {
  getDashboardButton,
  getDashboardHeader,
  getDashboardSubtitle,
} from 'Modules/dashboard/utils/dashboard.utils'

import { Banner } from 'Components/_theme'

import { useStore } from 'Stores/index'

import { useTheme } from 'Hooks'

import { isActiveLanguage, isOrderLimitExceeded } from 'Utilities'

import { SupportedLanguagesEnum } from 'Constants/locale'

import Img from 'Assets/images/dashboard/employee_banner.png'
import ImgTelekom from 'Assets/images/dashboard/employee_banner_telekom.png'

import { createLimitationRules } from 'Portal/src/stores/orderStore/orderStoreLimitationRules.utils'

import type { DashboardEmployeeBannerProps } from './DashboardEmployeeBanner.types'

export const DashboardEmployeeBanner = observer(
  (props: DashboardEmployeeBannerProps) => {
    const { onNewOrderButtonClick, employee, version } = props

    const { portalContentStore, portalConfigStore } = useStore()
    const { portalContent } = portalContentStore
    const { portalConfig } = portalConfigStore

    const intl = useIntl()

    const { isTelekomTheme } = useTheme()

    const isGermanLanguageActive = isActiveLanguage(SupportedLanguagesEnum.DE)

    const renderHeader = useMemo(() => {
      return getDashboardHeader(
        intl,
        isGermanLanguageActive,
        portalContent,
        'EmployeeDashboard_title',
      )
    }, [intl, portalContent, isGermanLanguageActive])

    const renderSubtitle = useMemo(() => {
      return getDashboardSubtitle(
        intl,
        isGermanLanguageActive,
        portalContent,
        'EmployeeDashboard_content',
      )
    }, [intl, portalContent, isGermanLanguageActive])

    const renderButton = useMemo(() => {
      return getDashboardButton(
        intl,
        isGermanLanguageActive,
        portalContent,
        'EmployeeDashboard_button',
      )
    }, [isGermanLanguageActive, portalContent, intl])

    const defaultImageSource = isTelekomTheme ? ImgTelekom : Img

    const showButton = useMemo(
      () =>
        employee.limitationRules?.limitationRule
          ? !isOrderLimitExceeded(
              createLimitationRules(
                employee.limitationRules.limitationRule,
                null,
              ),
              portalConfig?.enableProductOffers && employee.canPurchase,
            )
          : true,
      [
        employee.canPurchase,
        employee.limitationRules?.limitationRule,
        portalConfig?.enableProductOffers,
      ],
    )

    return (
      <Banner
        alt={
          portalContent?.dashboardImageURL
            ? renderSubtitle
            : intl.formatMessage({ id: 'Img_alt_dashboard_banner' })
        }
        badge={intl.formatMessage({ id: 'EmployeeDashboard_tag' })}
        buttonText={renderButton}
        className="mb-6 ps-0"
        imageSource={
          portalContent?.dashboardImageURL
            ? portalContent.dashboardImageURL
            : defaultImageSource
        }
        onButtonClick={onNewOrderButtonClick}
        showButton={showButton}
        text={renderSubtitle}
        title={renderHeader}
        version={version}
      />
    )
  },
)
